import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  Target, Plus, Check, Trophy, Trash2, Edit2, X,
  ChevronDown, ChevronUp, Flame, Medal, Dumbbell, Brain, User
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

const CATEGORIES = [
  { id: 'performance', label: 'Performance', icon: Trophy, color: 'text-yellow-500' },
  { id: 'fitness', label: 'Fitness', icon: Dumbbell, color: 'text-[#02b5ff]' },
  { id: 'skills', label: 'Skills', icon: Target, color: 'text-green-500' },
  { id: 'mental', label: 'Mental', icon: Brain, color: 'text-purple-500' },
  { id: 'personal', label: 'Personal', icon: User, color: 'text-orange-500' }
];

const PRESET_TARGETS = [
  { title: 'Score goals this season', unit: 'goals', category: 'performance', target_value: 10 },
  { title: 'Assists this season', unit: 'assists', category: 'performance', target_value: 5 },
  { title: 'Clean sheets (GK)', unit: 'clean sheets', category: 'performance', target_value: 8 },
  { title: 'Training sessions attended', unit: 'sessions', category: 'fitness', target_value: 40 },
  { title: 'Personal bests achieved', unit: 'PBs', category: 'fitness', target_value: 5 },
  { title: 'New skills mastered', unit: 'skills', category: 'skills', target_value: 3 },
  { title: 'Matches played', unit: 'matches', category: 'performance', target_value: 20 }
];

export default function PlayerTargets({ childId, childName }) {
  const [targets, setTargets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['performance', 'fitness']);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'performance',
    target_value: '',
    unit: '',
    deadline: ''
  });

  const fetchTargets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/targets/${childId}`);
      setTargets(res.data.targets || []);
    } catch (err) {
      console.error('Failed to fetch targets');
    } finally {
      setLoading(false);
    }
  }, [childId]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/targets/stats/${childId}`);
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats');
    }
  }, [childId]);

  useEffect(() => {
    if (childId) {
      fetchTargets();
      fetchStats();
    }
  }, [childId, fetchTargets, fetchStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a target title');
      return;
    }

    try {
      if (editingTarget) {
        await axios.put(`${API}/targets/${editingTarget.id}`, {
          title: formData.title,
          description: formData.description,
          category: formData.category,
          target_value: formData.target_value ? parseFloat(formData.target_value) : null,
          unit: formData.unit,
          deadline: formData.deadline || null
        });
        toast.success('Target updated!');
      } else {
        await axios.post(`${API}/targets`, {
          child_id: childId,
          title: formData.title,
          description: formData.description,
          category: formData.category,
          target_value: formData.target_value ? parseFloat(formData.target_value) : null,
          unit: formData.unit,
          deadline: formData.deadline || null
        });
        toast.success('Target created!');
      }
      
      setShowAddForm(false);
      setEditingTarget(null);
      resetForm();
      fetchTargets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to save target');
    }
  };

  const handleProgress = async (targetId, increment = 1) => {
    try {
      const res = await axios.post(`${API}/targets/${targetId}/progress`, { increment });
      
      if (res.data.completed) {
        toast.success('Target completed! 🎉');
      } else {
        toast.success('Progress updated!');
      }
      
      fetchTargets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update progress');
    }
  };

  const handleComplete = async (targetId, completed) => {
    try {
      await axios.put(`${API}/targets/${targetId}`, { completed });
      toast.success(completed ? 'Target marked complete!' : 'Target reopened');
      fetchTargets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update target');
    }
  };

  const handleDelete = async (targetId) => {
    if (!window.confirm('Delete this target?')) return;
    
    try {
      await axios.delete(`${API}/targets/${targetId}`);
      toast.success('Target deleted');
      fetchTargets();
      fetchStats();
    } catch (err) {
      toast.error('Failed to delete target');
    }
  };

  const handleEdit = (target) => {
    setFormData({
      title: target.title,
      description: target.description || '',
      category: target.category,
      target_value: target.target_value || '',
      unit: target.unit || '',
      deadline: target.deadline || ''
    });
    setEditingTarget(target);
    setShowAddForm(true);
  };

  const applyPreset = (preset) => {
    setFormData({
      title: preset.title,
      description: '',
      category: preset.category,
      target_value: preset.target_value,
      unit: preset.unit,
      deadline: ''
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'performance',
      target_value: '',
      unit: '',
      deadline: ''
    });
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => 
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const groupedTargets = CATEGORIES.reduce((acc, cat) => {
    acc[cat.id] = targets.filter(t => t.category === cat.id);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 animate-pulse">
        <div className="h-6 bg-zinc-800 rounded w-40 mb-4" />
        <div className="space-y-3">
          <div className="h-16 bg-zinc-800 rounded" />
          <div className="h-16 bg-zinc-800 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="player-targets">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
              <Target className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white">Season Targets</h3>
              <p className="text-xs text-zinc-400">Set goals and track your progress</p>
            </div>
          </div>
          <Button
            onClick={() => { setShowAddForm(true); setEditingTarget(null); resetForm(); }}
            size="sm"
            className="bg-yellow-500 hover:bg-yellow-600 text-black rounded-full text-xs"
          >
            <Plus className="h-3 w-3 mr-1" /> Add Target
          </Button>
        </div>

        {/* Stats Summary */}
        {stats && (
          <div className="grid grid-cols-4 gap-2 mt-3">
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-white">{stats.total_targets}</div>
              <div className="text-[10px] text-zinc-500">Total</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-green-500">{stats.completed_targets}</div>
              <div className="text-[10px] text-zinc-500">Completed</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-yellow-500">{stats.total_targets - stats.completed_targets}</div>
              <div className="text-[10px] text-zinc-500">In Progress</div>
            </div>
            <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
              <div className="text-lg font-bold text-[#02b5ff]">{stats.completion_rate}%</div>
              <div className="text-[10px] text-zinc-500">Success Rate</div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="p-4 border-b border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-white">
              {editingTarget ? 'Edit Target' : 'New Target'}
            </h4>
            <button onClick={() => { setShowAddForm(false); setEditingTarget(null); }} className="text-zinc-400 hover:text-white">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Presets */}
          {!editingTarget && (
            <div className="mb-4">
              <p className="text-xs text-zinc-500 mb-2">Quick Add:</p>
              <div className="flex flex-wrap gap-1">
                {PRESET_TARGETS.slice(0, 4).map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => applyPreset(preset)}
                    className="text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 px-2 py-1 rounded"
                  >
                    {preset.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Target title *"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="col-span-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none"
              />
              
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
              
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Target value"
                  value={formData.target_value}
                  onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                  className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none"
                />
                <input
                  type="text"
                  placeholder="Unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-20 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none"
                />
              </div>
            </div>

            <textarea
              placeholder="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-yellow-500 outline-none resize-none"
            />

            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setShowAddForm(false); setEditingTarget(null); }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                {editingTarget ? 'Update' : 'Create'} Target
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Targets List by Category */}
      <div className="divide-y divide-zinc-800">
        {CATEGORIES.map(category => {
          const catTargets = groupedTargets[category.id] || [];
          const Icon = category.icon;
          const isExpanded = expandedCategories.includes(category.id);
          
          if (catTargets.length === 0) return null;
          
          return (
            <div key={category.id}>
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full p-3 flex items-center justify-between hover:bg-zinc-800/30"
              >
                <div className="flex items-center gap-2">
                  <Icon className={`h-4 w-4 ${category.color}`} />
                  <span className="text-sm font-medium text-white">{category.label}</span>
                  <span className="text-xs text-zinc-500">({catTargets.length})</span>
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-zinc-500" /> : <ChevronDown className="h-4 w-4 text-zinc-500" />}
              </button>
              
              {isExpanded && (
                <div className="px-3 pb-3 space-y-2">
                  {catTargets.map(target => (
                    <TargetCard
                      key={target.id}
                      target={target}
                      onProgress={handleProgress}
                      onComplete={handleComplete}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {targets.length === 0 && (
        <div className="p-8 text-center">
          <Target className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
          <h4 className="font-semibold text-white mb-1">No targets set yet</h4>
          <p className="text-sm text-zinc-500 mb-4">
            Set season goals like "Score 10 goals" or "Attend 40 training sessions"
          </p>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            <Plus className="h-4 w-4 mr-2" /> Create First Target
          </Button>
        </div>
      )}
    </div>
  );
}

function TargetCard({ target, onProgress, onComplete, onEdit, onDelete }) {
  const progress = target.progress_percent || 0;
  const isComplete = target.completed;
  
  return (
    <div className={`bg-zinc-800/50 rounded-lg p-3 ${isComplete ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            {isComplete && <Check className="h-4 w-4 text-green-500" />}
            <h5 className={`font-medium ${isComplete ? 'text-zinc-400 line-through' : 'text-white'}`}>
              {target.title}
            </h5>
          </div>
          {target.description && (
            <p className="text-xs text-zinc-500 mt-1">{target.description}</p>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          <button
            onClick={() => onEdit(target)}
            className="p-1 text-zinc-500 hover:text-white"
          >
            <Edit2 className="h-3 w-3" />
          </button>
          <button
            onClick={() => onDelete(target.id)}
            className="p-1 text-zinc-500 hover:text-[#ff025f]"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Progress */}
      {target.target_value && (
        <div className="mb-2">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-zinc-400">
              {target.current_value || 0} / {target.target_value} {target.unit}
            </span>
            <span className={progress >= 100 ? 'text-green-500' : 'text-[#02b5ff]'}>
              {Math.round(progress)}%
            </span>
          </div>
          <Progress 
            value={progress} 
            className="h-2 bg-zinc-700"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-2">
        {!isComplete && target.target_value && (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onProgress(target.id, 1)}
              className="text-xs h-7 px-2"
            >
              <Plus className="h-3 w-3 mr-1" /> 1
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onProgress(target.id, 5)}
              className="text-xs h-7 px-2"
            >
              <Plus className="h-3 w-3 mr-1" /> 5
            </Button>
          </>
        )}
        
        <button
          onClick={() => onComplete(target.id, !isComplete)}
          className={`ml-auto text-xs px-2 py-1 rounded ${
            isComplete 
              ? 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600' 
              : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
          }`}
        >
          {isComplete ? 'Reopen' : 'Mark Complete'}
        </button>
      </div>
    </div>
  );
}
